import React, { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import { Button } from "@shadcn-in-nx/ui/button";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Plus, X, MessageSquare, Copy, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import Message from "../../components/Message";

const formatPhoneNumber = (phone) => {
    // Remove all non-digit characters except +
    const cleaned = phone.replace(/[^\d+]/g, "");

    // Ensure it starts with +
    const withPlus = cleaned.startsWith("+") ? cleaned : `+${cleaned}`;

    if (phone.trim() === "*") return "Anyone";

    // Format common patterns
    if (withPlus.length >= 12 && withPlus.startsWith("+1")) {
        // US/Canada format: +1 (XXX) XXX-XXXX
        const match = withPlus.match(/^\+1(\d{3})(\d{3})(\d{4})(.*)$/);
        if (match) {
            return `+1 (${match[1]}) ${match[2]}-${match[3]}${
                match[4] ? ` ${match[4]}` : ""
            }`;
        }
    } else if (withPlus.length >= 10) {
        // International format: +XX XXX XXX XXXX
        const countryCode = withPlus.substring(1, withPlus.length - 9);
        const remaining = withPlus.substring(withPlus.length - 9);
        const match = remaining.match(/^(\d{3})(\d{3})(\d{3})(.*)$/);
        if (match) {
            return `+${countryCode} ${match[1]} ${match[2]} ${match[3]}${
                match[4] ? ` ${match[4]}` : ""
            }`;
        }
    }

    // Fallback: just ensure it has + and add some spacing
    return withPlus.replace(/(\+\d{1,3})(\d{3})(\d{3})(\d+)/, "$1 $2 $3 $4");
};

export default function WhatsAppTriggerModal({
    isOpen,
    handleCancel,
    onClose,
    onConfirm,
    selectedNumber,
    existingTrigger = null, // For editing existing triggers
    isEditMode = false,
}) {
    const [allowedPhones, setAllowedPhones] = useState([]);
    const [newPhone, setNewPhone] = useState("");
    const [error, setError] = useState(null);

    // Initialize with existing phone numbers when in edit mode
    React.useEffect(() => {
        if (isEditMode && existingTrigger?.whatsappTrigger?.allowedPhones) {
            setAllowedPhones(existingTrigger.whatsappTrigger.allowedPhones);
        } else {
            setAllowedPhones([]);
        }
    }, [isEditMode, existingTrigger, isOpen]);

    // Format phone number to be more user-friendly

    // Copy phone number to clipboard
    const copyPhoneNumber = (phone) => {
        // Copy the raw number (cleaned)
        const cleanNumber = phone.replace(/[^\d+]/g, "");
        navigator.clipboard.writeText(cleanNumber);
        toast.success("Phone number copied to clipboard!");
    };

    const handleAddPhone = () => {
        if (!newPhone.trim()) {
            toast.error("Please enter a phone number");
            return;
        }

        // Clean and format the phone number
        const cleaned = newPhone.replace(/[^\d+]/g, "");
        const withPlus = cleaned.startsWith("+") ? cleaned : `+${cleaned}`;

        // Basic phone number validation
        const phoneRegex = /^[+][1-9][\d]{6,15}$/;
        if (!phoneRegex.test(withPlus) && newPhone !== "*") {
            toast.error("Please enter a valid phone number with country code");
            return;
        }

        if (allowedPhones.includes(withPlus)) {
            toast.error("Phone number already added");
            return;
        }

        if (newPhone === "*") {
            setAllowedPhones((prev) => [...prev, newPhone]);
            setNewPhone("");
            toast.success("Phone number added");
            return;
        }

        setAllowedPhones((prev) => [...prev, withPlus]);
        setNewPhone("");
        toast.success("Phone number added");
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && newPhone) {
            e.preventDefault();
            handleAddPhone();
        }
    };

    const handleRemovePhone = (phoneToRemove) => {
        setAllowedPhones(
            allowedPhones.filter((phone) => phone !== phoneToRemove)
        );
        toast.success("Phone number removed");
    };

    const handleConfirm = async () => {
        if (allowedPhones.length === 0) {
            toast.error("Please add at least one allowed phone number");
            return;
        }

        try {
            await onConfirm(allowedPhones);
            onClose();

            // Reset state
            setAllowedPhones([]);
            setNewPhone("");
        } catch (err) {
            setError(err);
        }
    };

    const handleCancelModal = () => {
        handleCancel();

        // Reset state
        setAllowedPhones([]);
        setNewPhone("");
    };

    if (!isOpen) return null;

    return (
        <Dialog open={isOpen} onOpenChange={handleCancelModal}>
            <DialogContent className="sm:max-w-md overflow-auto max-h-full">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                        <div className="bg-green-100 p-2 rounded-lg">
                            <MessageSquare className="h-5 w-5 text-green-600" />
                        </div>
                        {isEditMode
                            ? "Edit WhatsApp Trigger"
                            : "Configure WhatsApp Trigger"}
                    </DialogTitle>
                    <DialogDescription>
                        Configure which phone numbers are allowed to send
                        messages to trigger your workflow.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Selected WhatsApp Number Info */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <MessageSquare className="h-4 w-4 text-green-600" />
                                <div>
                                    <span className="font-medium text-green-900">
                                        WhatsApp Number:{" "}
                                        {formatPhoneNumber(
                                            selectedNumber?.number || ""
                                        )}
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={() =>
                                    copyPhoneNumber(
                                        selectedNumber?.number || ""
                                    )
                                }
                                className="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-100 transition-colors rounded"
                                title="Copy WhatsApp number"
                            >
                                <Copy className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    {/* Add Phone Number Input */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <Label htmlFor="newPhone">
                                Add Allowed Phone Number
                            </Label>
                            <div className="relative group">
                                <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                    Only messages from these phone numbers will
                                    trigger your workflow. Messages from other
                                    numbers will be ignored.
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Input
                                id="newPhone"
                                placeholder="e.g., +1 234 567 8900 or 1234567890"
                                value={newPhone}
                                onChange={(e) => setNewPhone(e.target.value)}
                                onKeyPress={handleKeyPress}
                                className="flex-1"
                            />
                            <Button
                                onClick={handleAddPhone}
                                disabled={!newPhone.trim()}
                                size="sm"
                            >
                                <Plus className="h-4 w-4" />
                            </Button>
                        </div>
                        <p className="text-xs text-gray-500">
                            Enter phone numbers with country code. The + will be
                            added automatically if missing.
                            <br />
                            Examples: +1 234 567 8900, 1234567890, +44 20 7946
                            0958
                        </p>
                    </div>

                    {/* Allowed Phone Numbers List */}
                    <div className="space-y-2">
                        <Label>
                            Allowed Phone Numbers ({allowedPhones.length})
                        </Label>
                        {allowedPhones.length > 0 ? (
                            <div className="space-y-2 max-h-40 overflow-y-auto">
                                {allowedPhones.map((phone, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="flex-1">
                                            <span className="text-sm font-medium text-gray-900">
                                                {formatPhoneNumber(phone)}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() =>
                                                    copyPhoneNumber(phone)
                                                }
                                                className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors rounded"
                                                title="Copy phone number"
                                            >
                                                <Copy className="h-3.5 w-3.5" />
                                            </button>
                                            <button
                                                onClick={() =>
                                                    handleRemovePhone(phone)
                                                }
                                                className="p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded"
                                                title="Remove phone number"
                                            >
                                                <X className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-4 text-gray-500 text-sm border-2 border-dashed border-gray-200 rounded-lg">
                                No phone numbers added yet
                            </div>
                        )}
                    </div>

                    {/* Info Box */}
                </div>

                {error ? (
                    <Message error>
                        {error?.message ?? error?.error ?? error}
                    </Message>
                ) : null}

                {/* Actions */}
                <div className="flex gap-2 pt-4">
                    <Button
                        variant="outline"
                        onClick={handleCancelModal}
                        className="flex-1"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={allowedPhones.length === 0}
                        className="flex-1"
                    >
                        {isEditMode ? "Update Trigger" : "Create Trigger"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
